import { useState } from 'react';
import { Check, Plus, X, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { useToast } from '../../ui/use-toast';
import { supabase } from '../../../lib/supabase';
import { getDomainFilterParams } from '../../../hooks/useDomainQuery';

interface Category {
  id: string;
  name: string;
}

interface ElearningCategorySelectProps {
  categories: Category[];
  selectedCategoryId: string;
  onChange: (categoryId: string) => void;
  onCategoryCreated?: (newCategory: Category) => void;
}

export const ElearningCategorySelect = ({
  categories,
  selectedCategoryId,
  onChange,
  onCategoryCreated,
}: ElearningCategorySelectProps) => {
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const createNewCategory = async () => {
    if (!newCategoryName.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Le nom de la catégorie ne peut pas être vide',
      });
      return;
    }

    setIsCreating(true);
    try {
      // Récupérer les paramètres de domaine
      const { domain, tenantId } = getDomainFilterParams();

      // Générer un slug
      const slug = newCategoryName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      // Obtenir le dernier sort_order
      let maxSortOrder = 0;
      const { data: existingCategories } = await supabase
        .from('elearn_categories')
        .select('sort_order')
        .order('sort_order', { ascending: false })
        .limit(1);

      if (existingCategories && existingCategories.length > 0) {
        maxSortOrder = existingCategories[0].sort_order || 0;
      }

      // Créer la catégorie
      const { data, error } = await supabase
        .from('elearn_categories')
        .insert({
          name: newCategoryName.trim(),
          slug: slug,
          description: '',
          is_active: true,
          sort_order: maxSortOrder + 1,
          domain: domain || null,
          tenant_id: parseInt(tenantId),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Catégorie créée avec succès',
      });

      // Créer l'objet catégorie
      const newCategory: Category = {
        id: data.id,
        name: data.name,
      };

      // Notifier le parent de la nouvelle catégorie
      if (onCategoryCreated) {
        onCategoryCreated(newCategory);
      }

      // Sélectionner automatiquement la nouvelle catégorie
      onChange(data.id);

      // Réinitialiser le formulaire
      setNewCategoryName('');
      setShowNewCategory(false);
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de créer la catégorie',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Sélection de catégorie existante - Style cards */}
      {categories.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-gray-400">Catégories disponibles</p>
          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-1">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => onChange(category.id)}
                className={`
                  relative p-3 rounded-lg border-2 transition-all duration-200 text-left
                  ${
                    selectedCategoryId === category.id
                      ? 'border-yellow-500 bg-yellow-500/10 text-white'
                      : 'border-white/10 bg-white/5 text-gray-300 hover:border-white/20 hover:bg-white/10'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{category.name}</span>
                  {selectedCategoryId === category.id && (
                    <Check className="h-4 w-4 text-yellow-500 flex-shrink-0 ml-2" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {categories.length === 0 && (
        <div className="text-center py-6 bg-white/5 border border-white/10 rounded-lg">
          <p className="text-gray-400 text-sm">Aucune catégorie disponible</p>
          <p className="text-gray-500 text-xs mt-1">Créez votre première catégorie ci-dessous</p>
        </div>
      )}

      {/* Formulaire de création de nouvelle catégorie */}
      {!showNewCategory ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowNewCategory(true)}
          className="bg-white/5 border-white/10 text-white hover:bg-white/10"
        >
          <Plus className="mr-2 h-4 w-4" />
          Créer une nouvelle catégorie
        </Button>
      ) : (
        <div className="space-y-2 p-3 bg-white/5 border border-white/10 rounded-lg">
          <div className="flex items-center space-x-2">
            <Input
              placeholder="Nom de la catégorie"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  createNewCategory();
                }
              }}
              className="flex-1 text-white bg-white/5 border-white/10 focus:border-yellow-500/50 focus:ring-yellow-500/50"
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowNewCategory(false);
                setNewCategoryName('');
              }}
              className="hover:bg-white/10"
            >
              <X className="h-4 w-4 text-white" />
            </Button>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={createNewCategory}
            disabled={isCreating || !newCategoryName.trim()}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Création...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Créer la catégorie
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ElearningCategorySelect;
